import { useMemo } from "react";
import { usePureStudentUrls } from "@crehana/auth";
import { getNextUrl } from "@crehana/utils";
import { DEFAULT_MAIN_MENU_URLS, USER_MENU_RENEWAL_ITEM_SOURCE_DETAIL } from "../../constants.js";
import { createUrlWithAmplitudeSource, createUrlWithCountryPrefix } from "../../utils/index.js";
const useMainMenuUrls = ({ customUrls, me, countryPrefix, amplitudePageName })=>{
    const { homeUrl, myCoursesUrl, myDiplomasUrl, myProjectsUrl } = usePureStudentUrls({
        me: {
            ...me,
            organization: me.organization
        }
    });
    const urls = useMemo(()=>{
        const nextUrl = getNextUrl();
        const login = nextUrl ? `${DEFAULT_MAIN_MENU_URLS.login}?next=${nextUrl}` : DEFAULT_MAIN_MENU_URLS.login;
        const register = nextUrl ? `${DEFAULT_MAIN_MENU_URLS.register}?next=${nextUrl}` : DEFAULT_MAIN_MENU_URLS.register;
        let home = DEFAULT_MAIN_MENU_URLS.homePage;
        if (!me.isAnonymous || me.isB2B) home = homeUrl;
        const mergedUrls = {
            ...DEFAULT_MAIN_MENU_URLS,
            login,
            register,
            home,
            premiumLanding: createUrlWithCountryPrefix({
                url: DEFAULT_MAIN_MENU_URLS.premiumLanding,
                countryPrefix
            }),
            crehanaAcademiesLanding: createUrlWithCountryPrefix({
                url: DEFAULT_MAIN_MENU_URLS.crehanaAcademiesLanding,
                countryPrefix
            }),
            blog: createUrlWithCountryPrefix({
                url: DEFAULT_MAIN_MENU_URLS.blog,
                countryPrefix
            }),
            B2BHomeUserKnowledgeHubCatalog: me?.organization?.slug ? DEFAULT_MAIN_MENU_URLS.createCatalogKnowledgeHubUrl(me.organization.slug) : '',
            B2BAdminDashboard: me?.organization?.slug ? DEFAULT_MAIN_MENU_URLS.createB2BAdminDashboardUrl(me.organization.slug) : '',
            B2BAdminDashboardLeader: me?.organization?.slug ? `${DEFAULT_MAIN_MENU_URLS.createB2BAdminDashboardUrl(me.organization.slug)}lider/` : '',
            ...customUrls,
            userDashboard: {
                ...DEFAULT_MAIN_MENU_URLS.userDashboard,
                ...customUrls?.userDashboard,
                diplomas: myDiplomasUrl,
                courses: myCoursesUrl,
                projects: myProjectsUrl
            },
            appDownload: {
                ...DEFAULT_MAIN_MENU_URLS.appDownload,
                ...customUrls?.appDownload
            },
            renewalLanding: createUrlWithCountryPrefix({
                url: DEFAULT_MAIN_MENU_URLS.renewalLanding,
                countryPrefix
            }),
            catalog: createUrlWithCountryPrefix({
                url: DEFAULT_MAIN_MENU_URLS.catalog,
                countryPrefix
            }),
            newCoursesCatalog: createUrlWithCountryPrefix({
                url: DEFAULT_MAIN_MENU_URLS.newCoursesCatalog,
                countryPrefix
            }),
            searchLanding: createUrlWithCountryPrefix({
                url: DEFAULT_MAIN_MENU_URLS.searchLanding,
                countryPrefix
            }),
            B2BPlansLanding: createUrlWithCountryPrefix({
                url: DEFAULT_MAIN_MENU_URLS.B2BPlansLanding,
                countryPrefix
            })
        };
        return {
            ...mergedUrls,
            userDashboard: {
                ...mergedUrls.userDashboard,
                courses: createUrlWithAmplitudeSource({
                    url: me.isDegreeStudent ? mergedUrls.userDashboard.microDegree : mergedUrls.userDashboard.courses,
                    amplitudePageName
                }),
                diplomas: createUrlWithAmplitudeSource({
                    url: mergedUrls.userDashboard.diplomas,
                    amplitudePageName
                }),
                projects: createUrlWithAmplitudeSource({
                    url: mergedUrls.userDashboard.projects,
                    amplitudePageName
                }),
                savedCourses: createUrlWithAmplitudeSource({
                    url: mergedUrls.userDashboard.savedCourses,
                    amplitudePageName
                })
            },
            login: createUrlWithAmplitudeSource({
                url: mergedUrls.login,
                amplitudePageName
            }),
            register: createUrlWithAmplitudeSource({
                url: mergedUrls.register,
                amplitudePageName
            }),
            catalog: createUrlWithAmplitudeSource({
                url: mergedUrls.catalog,
                amplitudePageName
            }),
            newCoursesCatalog: createUrlWithAmplitudeSource({
                url: mergedUrls.newCoursesCatalog,
                amplitudePageName
            }),
            premiumRefer: createUrlWithAmplitudeSource({
                url: mergedUrls.premiumRefer,
                amplitudePageName
            }),
            crehanaLive: createUrlWithAmplitudeSource({
                url: mergedUrls.crehanaLive,
                amplitudePageName
            }),
            resources: createUrlWithAmplitudeSource({
                url: mergedUrls.resources,
                amplitudePageName
            }),
            courseAgenda: createUrlWithAmplitudeSource({
                url: mergedUrls.courseAgenda,
                amplitudePageName
            }),
            inbox: createUrlWithAmplitudeSource({
                url: mergedUrls.inbox,
                amplitudePageName
            }),
            shoppingCart: createUrlWithAmplitudeSource({
                url: mergedUrls.shoppingCart,
                amplitudePageName
            }),
            configuration: createUrlWithAmplitudeSource({
                url: mergedUrls.configuration,
                amplitudePageName
            }),
            legal: createUrlWithAmplitudeSource({
                url: mergedUrls.legal,
                amplitudePageName
            }),
            premiumLanding: createUrlWithAmplitudeSource({
                url: mergedUrls.premiumLanding,
                amplitudePageName
            }),
            crehanaAcademiesLanding: createUrlWithAmplitudeSource({
                url: mergedUrls.crehanaAcademiesLanding,
                amplitudePageName
            }),
            blog: createUrlWithAmplitudeSource({
                url: mergedUrls.blog,
                amplitudePageName
            }),
            renewalLanding: createUrlWithAmplitudeSource({
                url: mergedUrls.renewalLanding,
                amplitudePageName,
                sourceDetail: USER_MENU_RENEWAL_ITEM_SOURCE_DETAIL
            }),
            B2BPlansLanding: createUrlWithAmplitudeSource({
                url: mergedUrls.B2BPlansLanding,
                amplitudePageName
            }),
            downloadableResources: createUrlWithAmplitudeSource({
                url: mergedUrls.downloadableResources,
                amplitudePageName
            }),
            higherEdLanding: createUrlWithAmplitudeSource({
                url: mergedUrls.higherEdLanding,
                amplitudePageName
            })
        };
    }, [
        customUrls,
        me,
        countryPrefix,
        amplitudePageName,
        homeUrl,
        myCoursesUrl,
        myDiplomasUrl,
        myProjectsUrl
    ]);
    return urls;
};
const hooks_useMainMenuUrls = useMainMenuUrls;
export default hooks_useMainMenuUrls;
